import { Button, Container } from '@playcanvas/pcui';

import { Events } from '../events';
import { SAMPLE_CACHE_NAME, SAMPLE_SCENES, SampleScene } from '../sample-scenes';
import logo from './biligoba-logo.png';

type CacheState = 'queued' | 'downloading' | 'ready' | 'error';

type CardElements = {
    button: HTMLButtonElement;
    status: HTMLSpanElement;
    progress: HTMLSpanElement;
};

const formatSize = (bytes: number) => {
    if (bytes >= 1024 ** 3) return `${(bytes / (1024 ** 3)).toFixed(2)} GB`;
    return `${Math.round(bytes / (1024 ** 2))} MB`;
};

// eslint-disable-next-line no-undef
const createElement = <K extends keyof HTMLElementTagNameMap>(
    tag: K,
    className?: string,
    text?: string
) => {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text) element.textContent = text;
    return element;
};

class SampleScenesDialog extends Container {
    private selectedId: SampleScene['id'] = SAMPLE_SCENES[0].id;
    private readonly cards = new Map<SampleScene['id'], CardElements>();
    private readonly events: Events;
    private readonly openButton: Button;
    private activeOpenId: SampleScene['id'] | null = null;
    private openGeneration = 0;

    constructor(events: Events, args = {}) {
        super({
            ...args,
            id: 'sample-scenes-dialog',
            hidden: true,
            tabIndex: -1
        });

        this.events = events;

        const dialog = createElement('section', 'sample-dialog');
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
        dialog.setAttribute('aria-labelledby', 'sample-dialog-title');
        dialog.setAttribute('aria-describedby', 'sample-dialog-description');

        const header = createElement('header', 'sample-dialog-header');
        const brand = createElement('div', 'sample-dialog-brand');
        const brandLogo = createElement('img', 'sample-dialog-logo');
        brandLogo.src = logo;
        brandLogo.alt = '';
        const brandText = createElement('span', 'sample-dialog-brand-name', 'Biligoba Scene Forensics');
        brand.append(brandLogo, brandText);

        const dismiss = createElement('button', 'sample-dialog-dismiss');
        dismiss.type = 'button';
        dismiss.textContent = 'Close';
        dismiss.setAttribute('aria-label', 'Close sample scene window');
        dismiss.addEventListener('click', () => this.hide());
        header.append(brand, dismiss);

        const intro = createElement('div', 'sample-dialog-intro');
        const title = createElement('h1', 'sample-dialog-title', 'Choose a scene');
        title.id = 'sample-dialog-title';
        const description = createElement(
            'p',
            'sample-dialog-description',
            'Open a prepared sample or import your own BLGS file.'
        );
        description.id = 'sample-dialog-description';
        intro.append(title, description);

        const sceneGrid = createElement('div', 'sample-scene-grid');
        sceneGrid.setAttribute('role', 'radiogroup');
        sceneGrid.setAttribute('aria-label', 'Sample scenes');

        for (const scene of SAMPLE_SCENES) {
            const card = createElement('button', 'sample-scene-card');
            card.type = 'button';
            card.setAttribute('role', 'radio');
            card.setAttribute('aria-checked', String(scene.id === this.selectedId));
            card.dataset.sceneId = scene.id;
            card.dataset.cacheState = 'queued';

            const preview = createElement('img', 'sample-scene-preview');
            preview.src = new URL(scene.previewUrl, document.baseURI).href;
            preview.alt = `${scene.name} sample scene preview`;

            const body = createElement('span', 'sample-scene-body');
            const headingRow = createElement('span', 'sample-scene-heading-row');
            const name = createElement('span', 'sample-scene-name', scene.name);
            const size = createElement('span', 'sample-scene-size', formatSize(scene.size));
            headingRow.append(name, size);

            const detail = createElement('span', 'sample-scene-detail', scene.description);
            const cacheRow = createElement('span', 'sample-scene-cache-row');
            const status = createElement('span', 'sample-scene-status', 'Available to download');
            const progressTrack = createElement('span', 'sample-scene-progress');
            const progress = createElement('span', 'sample-scene-progress-fill');
            progressTrack.append(progress);
            cacheRow.append(status, progressTrack);
            body.append(headingRow, detail, cacheRow);
            card.append(preview, body);

            card.addEventListener('click', () => this.select(scene.id));
            card.addEventListener('keydown', event => this.handleCardKeydown(event, scene.id));
            sceneGrid.append(card);
            this.cards.set(scene.id, { button: card, status, progress });
        }

        const cacheNote = createElement('p', 'sample-cache-note');
        cacheNote.textContent = 'A selected sample downloads once, then stays available in this browser.';

        const footer = createElement('footer', 'sample-dialog-footer');
        const importGroup = createElement('div', 'sample-import-group');
        const importLabel = createElement('span', 'sample-import-label', 'Have a scene already?');
        const importButton = new Button({
            class: 'sample-secondary-button',
            text: 'Import BLGS'
        });
        importButton.on('click', async () => {
            await events.invoke('blg.open');
        });
        importGroup.append(importLabel, importButton.dom);

        this.openButton = new Button({
            class: 'sample-primary-button',
            text: `Open ${SAMPLE_SCENES[0].name}`
        });
        this.openButton.on('click', () => {
            this.openSelected().catch(error => console.error(error));
        });
        footer.append(importGroup, this.openButton.dom);

        dialog.append(header, intro, sceneGrid, cacheNote, footer);
        this.dom.append(dialog);

        this.dom.addEventListener('keydown', (event: KeyboardEvent) => {
            if (this.hidden) return;
            if (event.key === 'Escape') {
                this.hide();
                event.preventDefault();
            }
            event.stopPropagation();
        });

        this.dom.addEventListener('pointerdown', (event: PointerEvent) => {
            if (event.target === this.dom) this.hide();
        });

        events.on('samples.show', () => this.show());
        events.on('samples.hide', () => this.hide());

        this.refreshCacheState();
    }

    private select(id: SampleScene['id']) {
        this.selectedId = id;
        for (const scene of SAMPLE_SCENES) {
            const selected = scene.id === id;
            const card = this.cards.get(scene.id);
            card?.button.setAttribute('aria-checked', String(selected));
            card?.button.classList.toggle('selected', selected);
        }
        const scene = SAMPLE_SCENES.find(candidate => candidate.id === id);
        if (scene) this.openButton.text = `Open ${scene.name}`;
    }

    private handleCardKeydown(event: KeyboardEvent, id: SampleScene['id']) {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
        const currentIndex = SAMPLE_SCENES.findIndex(scene => scene.id === id);
        const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
        const nextIndex = (currentIndex + direction + SAMPLE_SCENES.length) % SAMPLE_SCENES.length;
        const nextScene = SAMPLE_SCENES[nextIndex];
        this.select(nextScene.id);
        this.cards.get(nextScene.id)?.button.focus();
        event.preventDefault();
    }

    private updateCacheState(id: SampleScene['id'], state: CacheState, progress = 0, message?: string) {
        const card = this.cards.get(id);
        if (!card) return;

        card.button.dataset.cacheState = state;
        card.progress.style.transform = `scaleX(${Math.max(0, Math.min(1, progress / 100))})`;

        if (state === 'ready') {
            card.status.textContent = 'Ready offline';
        } else if (state === 'downloading') {
            card.status.textContent = `Downloading ${Math.round(progress)}%`;
        } else if (state === 'error') {
            card.status.textContent = message || 'Available online';
        } else {
            card.status.textContent = 'Available to download';
        }
    }

    private async refreshCacheState() {
        if (!('caches' in window)) return;
        for (const scene of SAMPLE_SCENES) {
            const url = new URL(scene.url, document.baseURI).href;
            const response = await caches.match(url);
            if (response) this.updateCacheState(scene.id, 'ready', 100);
        }
    }

    private async openSelected() {
        const scene = SAMPLE_SCENES.find(candidate => candidate.id === this.selectedId);
        if (!scene || this.activeOpenId) return;

        const generation = ++this.openGeneration;
        let download: XMLHttpRequest | null = null;
        this.activeOpenId = scene.id;
        this.openButton.enabled = false;
        this.events.fire('progressStart', `Preparing ${scene.name}`);
        this.events.fire('progressUpdate', { text: 'Checking offline cache', progress: 0 });
        this.events.fire('progressShowCancel', () => {
            if (generation !== this.openGeneration) return;
            download?.abort();
            this.openGeneration++;
            this.activeOpenId = null;
            this.openButton.enabled = true;
            this.updateCacheState(scene.id, 'queued');
            this.events.fire('progressEnd');
        });

        try {
            if (navigator.storage?.persist) {
                try {
                    await navigator.storage.persist();
                } catch (error) {
                    console.warn('[SampleScenes] Persistent storage was not granted.', error);
                }
            }
            const url = new URL(scene.url, document.baseURI).href;
            const cachedResponse = 'caches' in window ? await caches.match(url) : undefined;
            if (!cachedResponse) this.updateCacheState(scene.id, 'downloading');
            this.events.fire('progressUpdate', {
                text: cachedResponse ? 'Opening cached scene' : `Downloading ${scene.name}`,
                progress: cachedResponse ? 100 : 0
            });

            const blob = cachedResponse ? await cachedResponse.blob() : await new Promise<Blob>((resolve, reject) => {
                const request = new XMLHttpRequest();
                download = request;
                request.open('GET', url);
                request.responseType = 'blob';
                request.onprogress = (event) => {
                    if (generation !== this.openGeneration) return;
                    const total = event.total || scene.size;
                    const progress = Math.min(99, (event.loaded / total) * 100);
                    this.events.fire('progressUpdate', {
                        text: `${formatSize(event.loaded)} / ${formatSize(total)}`,
                        progress
                    });
                    this.updateCacheState(scene.id, 'downloading', progress);
                };
                request.onload = () => {
                    download = null;
                    if (request.status >= 200 && request.status < 300) {
                        resolve(request.response);
                    } else {
                        reject(new Error(`Server returned ${request.status}`));
                    }
                };
                request.onerror = () => reject(new Error('Network request failed'));
                request.onabort = () => reject(new DOMException('Download cancelled', 'AbortError'));
                request.send();
            });
            if (generation !== this.openGeneration) return;

            this.events.fire('progressUpdate', { text: 'Opening scene', progress: 100 });

            const file = new File([blob], scene.filename, {
                type: blob.type || 'application/octet-stream'
            });

            this.hide();
            this.events.fire('progressEnd');
            this.activeOpenId = null;
            this.openButton.enabled = true;
            await this.events.invoke('blg.load', file);

            if (!cachedResponse && 'caches' in window) {
                caches.open(SAMPLE_CACHE_NAME)
                .then(cache => cache.put(url, new Response(blob, {
                    headers: { 'Content-Type': blob.type || 'application/octet-stream' }
                })))
                .then(() => this.updateCacheState(scene.id, 'ready', 100))
                .catch((error) => {
                    console.warn(`[SampleScenes] Could not cache ${scene.filename}.`, error);
                    this.updateCacheState(scene.id, 'error', 0, 'Cache unavailable');
                });
            }
        } catch (error) {
            if (generation !== this.openGeneration) return;
            console.error('[SampleScenes] Failed to open sample.', error);
            this.events.fire('progressEnd');
            this.hide();
            await this.events.invoke('showPopup', {
                type: 'error',
                header: 'Unable to open sample',
                message: `The ${scene.name} scene could not be downloaded. Check the server and available browser storage.`
            });
            if (this.events.invoke('scene.empty')) this.show();
        } finally {
            if (generation === this.openGeneration) {
                this.activeOpenId = null;
                this.openButton.enabled = true;
            }
        }
    }

    show() {
        this.hidden = false;
        this.select(this.selectedId);
        setTimeout(() => this.cards.get(this.selectedId)?.button.focus(), 0);
        this.refreshCacheState();
    }

    hide() {
        this.hidden = true;
    }
}

export { SampleScenesDialog };
